/**
 * Catálogo de permissões dos restaurantes. A API valida cada rota contra estas chaves;
 * a interface só as usa para esconder o que o usuário não pode fazer.
 */
export const PERMISSIONS = {
  // Estabelecimento
  'restaurant.settings.view': 'Ver configurações do estabelecimento',
  'restaurant.settings.edit': 'Editar dados, identidade visual e horários',
  'staff.view': 'Ver funcionários',
  'staff.manage': 'Cadastrar, editar e bloquear funcionários',
  'roles.manage': 'Criar e alterar funções e permissões',
  'audit.view': 'Ver auditoria',
  // Cardápio
  'menu.view': 'Ver cardápio administrativo',
  'menu.edit': 'Editar categorias, produtos e adicionais',
  'menu.availability': 'Pausar e liberar produtos',
  // Salão
  'tables.view': 'Ver mesas',
  'tables.manage': 'Cadastrar mesas e gerar QR Codes',
  'sessions.open': 'Abrir sessões de consumo',
  'sessions.close': 'Encerrar sessões sem saldo',
  // Pedidos e produção
  'orders.view': 'Ver pedidos',
  'orders.create': 'Lançar pedidos em nome do cliente',
  'orders.cancel_item': 'Cancelar itens de pedido',
  'production.kitchen': 'Operar a tela da cozinha',
  'production.bar': 'Operar a tela do bar',
  'printing.reprint': 'Reimprimir comandas',
  'printing.manage': 'Configurar impressoras e agentes',
  // Financeiro
  'accounts.view': 'Ver contas e saldos',
  'payments.manual.create': 'Registrar pagamentos presenciais',
  'payments.refund': 'Solicitar estornos',
  'discounts.apply': 'Aplicar descontos',
  'account.close.override': 'Encerrar conta com saldo (exceção auditada)',
  'payments.integration.manage': 'Conectar e desconectar o Mercado Pago',
  'exit.validate': 'Validar comprovantes de saída',
  // Relatórios
  'dashboard.view': 'Ver dashboard operacional',
  'reports.financial.view': 'Ver valores financeiros e relatórios',
  'reports.export': 'Exportar relatórios',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export function isPermission(value: string): value is Permission {
  return value in PERMISSIONS;
}

export type DefaultRoleKey = 'admin' | 'manager' | 'waiter' | 'kitchen' | 'bar' | 'cashier';

export const DEFAULT_ROLES: Record<DefaultRoleKey, { name: string; permissions: Permission[] }> = {
  admin: { name: 'Administrador', permissions: ALL_PERMISSIONS },
  manager: {
    name: 'Gerente',
    permissions: [
      'restaurant.settings.view', 'staff.view', 'staff.manage', 'audit.view',
      'menu.view', 'menu.edit', 'menu.availability',
      'tables.view', 'tables.manage', 'sessions.open', 'sessions.close',
      'orders.view', 'orders.create', 'orders.cancel_item', 'printing.reprint',
      'accounts.view', 'payments.manual.create', 'discounts.apply', 'exit.validate',
      'dashboard.view', 'reports.financial.view', 'reports.export',
    ],
  },
  waiter: {
    name: 'Garçom',
    permissions: ['menu.view', 'tables.view', 'sessions.open', 'orders.view', 'orders.create', 'accounts.view'],
  },
  kitchen: { name: 'Cozinha', permissions: ['orders.view', 'production.kitchen', 'printing.reprint'] },
  bar: { name: 'Bar', permissions: ['orders.view', 'production.bar', 'printing.reprint'] },
  cashier: {
    name: 'Caixa/Balcão',
    permissions: ['tables.view', 'orders.view', 'accounts.view', 'payments.manual.create', 'sessions.close', 'exit.validate'],
  },
};
